import { PerformSearchData, CurrentSearchData, events } from "solostar-lib";
import { qs } from "solostar-lib";

import {
  EventName,
  Scope,
  SearchGroup,
  FacetType,
  SearchField,
  SearchFields,
  SearchFieldEntity,
  SearchFieldFacet,
} from "solostar-graphql";

export const performSearch = (data: PerformSearchData) => {
  events.emit({ name: EventName.SS_PERFORM_SEARCH, data }, "top");
};

type SearchFieldsExtended = SearchFields &
  Record<string, string | string[] | SearchFieldEntity[] | SearchFieldFacet[]>;

export const dedupeSearchGroups = (groups: SearchGroup[]) => {
  const dict: Record<string, SearchGroup> = {};
  groups.forEach((group) => {
    const isSpecific = group.scope && group.scope !== Scope.APP;
    if (dict[group.id] && !isSpecific) {
      return;
    }
    dict[group.id] = group;
  });
  return Object.values(dict);
};

export const getKeywords = (group?: SearchGroup | null): string | null => {
  if (!group) {
    return null;
  }
  const query = group.queries.find((q) => q.facetType === FacetType.KEYWORDS);
  if (!query?.query) {
    return null;
  }
  return query.query;
};

const areEntitiesEqual = (a: SearchFieldEntity, b: SearchFieldEntity) => {
  if (a.text || b.text) {
    return (
      a.text?.toLowerCase() === b.text?.toLowerCase() && a.negated === b.negated
    );
  }
  return a.entity === b.entity;
};

const getKeywordsString = (names: (string | null)[], isNegated = false) => {
  const base = names
    .filter((n) => !!n)
    .map((e) => `"${e}"`)
    .join(" OR ");
  return isNegated ? `NOT (${base})` : base;
};

const checkSearchField = (
  field: SearchField,
  current: SearchFieldsExtended
) => {
  if ("value" in field.data) {
    const currentValue = current[field.data.key];
    if (typeof currentValue === "string" && !!currentValue.trim()) {
      return qs
        .sanitizeQueryString(currentValue ?? "")
        .includes(qs.sanitizeQueryString(field.data.value ?? ""));
    }
    return false;
  }
  if ("values" in field.data) {
    /** Hack for Greenhouse where companies and other entities are treated like keywords. */
    const asKeywords = getKeywordsString(field.data.values);
    if (current.keywords?.includes(asKeywords)) {
      return true;
    }

    const currentValues = current[field.data.key] as string[];
    if (Array.isArray(currentValues)) {
      return field.data.values.every((value) => currentValues.includes(value));
    }
    return false;
  }
  if ("entities" in field.data) {
    /** Hack for Greenhouse where companies and other entities are treated like keywords. */
    const asKeywords = getKeywordsString(
      field.data.entities.map((e) => e.text),
      field.data.entities.some((e) => e.negated)
    );
    /**
     * Check if the keywords in GH include this search suggestion, but also check to ensure
     * they don't include the negated version of it (as the negated version will have a NOT
     * already in the `asKeywords` string.)
     */
    if (
      current.keywords?.includes(asKeywords) &&
      !current.keywords?.includes(`NOT (${asKeywords})`)
    ) {
      return true;
    }

    const currentEntities = current[field.data.key] as SearchFieldEntity[];
    if (!currentEntities || !Array.isArray(currentEntities)) {
      return false;
    }
    return field.data.entities.every(
      (a) => !!currentEntities.find((b) => areEntitiesEqual(a, b))
    );
  }
  if ("facets" in field.data) {
    const currentFacets = current[field.data.type] as SearchFieldFacet[];
    return field.data.facets.every(
      (searchGroupFacet) =>
        !!currentFacets.find(
          (currentFacet) => currentFacet.value === searchGroupFacet.value
        )
    );
  }
  return false;
};

export const isSearchGroupChecked = (
  group: SearchGroup,
  current?: CurrentSearchData
) => {
  /** Using the new API-driven way of updated the LinkedIn search. */
  if (group.fields && current?.searchFields) {
    const allSatisfied = group.fields.every((field) =>
      checkSearchField(field, current.searchFields as SearchFieldsExtended)
    );
    return allSatisfied;
  }
  const query = group.queries[0];
  if (!query) {
    return false;
  }
  return (
    (query.query && current?.[query?.facetType]?.includes(query.query)) || false
  );
};
